import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CASINO_GAME_TEMPLATE,
  decentralizedGameCategoriesMapper,
  gameMapper
} from 'utils/casino/maps';
import {
  CUSTOM_CATEGORIES,
  CUSTOM_CATEGORY_IDS
} from 'utils/constants/casino/custom-categories';
import { ECasinoQueryKey } from 'utils/constants/store/query-keys';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import type {
  DecentralizedCasinoGamesData,
  IGetCasinoGamesResponse,
  TNewCasinoData,
  TNewCasinoFailedData
} from 'interfaces/new-casino';
import {
  DecentralizedCasinoGame,
  ENewCasinoFailedDataMessage,
  ENewCasinoGamesFetchingParamsKey,
  NewCasinoGame,
  TNewCasinoDataByAllGroup
} from 'interfaces/new-casino';
import { useCombineFavoriteGames } from './useCombineFavoriteGames';
import { useSelectedCategory } from './useSelectedCategory';
import {
  CASINO_PROVIDER_CRASH_GAMES,
  useCasinoOnlinePlayers
} from 'newhooks/casino/useCasinoOnlinePlayers';
import { useQueryClient } from 'newhooks/generic/useQueryClient';
import { useCasinoGamesConditionalsValues } from '../providers/CasinoGamesConditionalsProvider';
import {
  CasinoGamesLoadMoreBtnType,
  useCasinoGamesConfigsValues
} from '../providers/CasinoGamesConfigsContext';
import { CasinoGamesConditionalsActionType } from '../state/conditionals-reducer';
import { checkBackendAnomaly } from '../utils/check-backend-anomaly';
import { checkIsSelectedCategoryFavorites } from '../utils/check-selected-category';
import type { AxiosResponse } from 'axios';

export type UseGamesCombinedDataProps = {
  data: AxiosResponse<IGetCasinoGamesResponse> | undefined;
  paramsWithoutPaginationStringified: string;
};

type TCasinoGamesCombinedDataPartition = Partial<
  Omit<
    AxiosResponse<Exclude<IGetCasinoGamesResponse, TNewCasinoFailedData>>,
    'data'
  >
> & {
  data: {
    status?: string;
    games: Record<
      string,
      {
        count: number;
        games: NewCasinoGame[];
      }
    >;
  };
};

export type TCasinoGamesCombinedData = {
  queryKey?: string;
  data?: TCasinoGamesCombinedDataPartition;
};

const isGamesGroupedByCategory = (
  data: IGetCasinoGamesResponse
): data is TNewCasinoDataByAllGroup => {
  return !Array.isArray(data as TNewCasinoDataByAllGroup);
};

const isGamesGroupedById = (
  data: IGetCasinoGamesResponse
): data is TNewCasinoData => {
  return (data as TNewCasinoData).total_count !== undefined;
};

const isGamesDecentralized = (
  data: IGetCasinoGamesResponse
): data is DecentralizedCasinoGamesData => {
  return typeof (data as DecentralizedCasinoGamesData).count === 'number';
};

const generateCategoryAllSelectorKey = (providers: string | undefined) => {
  return `${CUSTOM_CATEGORIES.all.id}-${providers}`;
};

const setUniqueGames = (games: NewCasinoGame[]) => {
  const uniqueGamesIds: string[] = [];
  const uniqueGames: NewCasinoGame[] = [];

  const crashGamesIds: number[] = [];

  games.forEach(game => {
    if (!uniqueGamesIds.includes(game.id)) {
      uniqueGamesIds.push(game.id);
      uniqueGames.push(game);

      if (game.categories.includes(CASINO_PROVIDER_CRASH_GAMES)) {
        crashGamesIds.push(+game.extearnal_game_id);
      }
    }
  });

  return {
    uniqueGames,
    crashGamesIds
  };
};

export const useGamesCombinedData = ({
  data: _data,
  paramsWithoutPaginationStringified
}: UseGamesCombinedDataProps) => {
  const { configs, computedConfigs } = useCasinoGamesConfigsValues();
  const { dispatchConditionals } = useCasinoGamesConditionalsValues();
  const { queryClient } = useQueryClient();

  const { updateCrashGamesIds } = useCasinoOnlinePlayers();

  const [combinedData, setCombinedData] = useState<TCasinoGamesCombinedData>(
    {}
  );

  useCombineFavoriteGames({ setCombinedData });

  // to prevent unnecessary re-renders
  const paramsWithoutPaginationStringifiedRef = useRef(
    paramsWithoutPaginationStringified
  );

  const _setUniqueGames = (games: NewCasinoGame[]) => {
    const { crashGamesIds, uniqueGames } = setUniqueGames(games);

    updateCrashGamesIds(crashGamesIds);

    return uniqueGames;
  };

  // layout effect is used to set data to empty object when specific conditions are not met
  useLayoutEffect(() => {
    if (SpringConfigs.MOCKED_DATA) {
      return;
    }

    if (checkIsSelectedCategoryFavorites(selectedCategoryRef.current)) {
      return;
    }

    paramsWithoutPaginationStringifiedRef.current =
      paramsWithoutPaginationStringified;

    const queryKey = [
      ECasinoQueryKey.CasinoGames,
      configs.moduleId,
      paramsWithoutPaginationStringified,
      0 // offset is 0 for ensuring first page is cached
    ];

    const cache = queryClient.getQueryCache();
    const cachedData = cache.findAll({
      queryKey,
      exact: true,
      fetchStatus: 'idle' // to ensure cache is present and currently not being updated
    });

    const parsedParams = JSON.parse(paramsWithoutPaginationStringified);

    // check for parsedParams length is because react query has an in-between case where it has only one parameter
    // in that case there is no fetch for the data and query is decided to be a miss
    if (
      !cachedData.length ||
      (Object.keys(parsedParams).length < 2 &&
        ![CUSTOM_CATEGORIES.special.id].includes(selectedCategoryRef.current))
    ) {
      setCombinedData({});
    }
  }, [paramsWithoutPaginationStringified]);

  // to write filter for custom categories;
  const { selectedCategory } = useSelectedCategory({ configs });
  const selectedCategoryRef = useRef(selectedCategory);

  useEffect(() => {
    selectedCategoryRef.current = selectedCategory;
  }, [selectedCategory]);

  useEffect(() => {
    if (SpringConfigs.MOCKED_DATA) {
      return;
    }

    if (!_data) {
      return;
    }

    if (checkBackendAnomaly(_data)) {
      const data = _data as AxiosResponse<TNewCasinoFailedData>;

      if (data.data.message === ENewCasinoFailedDataMessage.TimeoutError) {
        dispatchConditionals({
          type: CasinoGamesConditionalsActionType.SET,
          payload: {
            emptyResults: true,
            queryLoading: undefined,
            loading: undefined
          }
        });
      }

      return;
    }

    const data = _data as AxiosResponse<
      Exclude<IGetCasinoGamesResponse, TNewCasinoFailedData>
    >;

    if (checkIsSelectedCategoryFavorites(selectedCategoryRef.current)) {
      return;
    }

    // when fetching direct games without grouping by categories and no games are returned
    // (slider type games for example)
    if (
      [ENewCasinoGamesFetchingParamsKey.Id].includes(data.config.params.by_key)
    ) {
      const gamesData = data.data;

      if (
        computedConfigs.loadMoreButtonType ===
          CasinoGamesLoadMoreBtnType.SLIDER &&
        isGamesGroupedById(gamesData) &&
        gamesData.games.length === 0
      ) {
        return;
      }
    }

    const isCustomCategory = CUSTOM_CATEGORY_IDS.includes(
      selectedCategoryRef.current
    );

    const isCustomCategoryAll =
      selectedCategoryRef.current === CUSTOM_CATEGORIES.all.id;

    const queryKey = isCustomCategory
      ? selectedCategoryRef.current
      : paramsWithoutPaginationStringifiedRef.current;

    const isDecentralizedEndpoint =
      SpringConfigs.DECENTRALIZED_CASINO_URL &&
      data.config.url?.includes(SpringConfigs.DECENTRALIZED_CASINO_URL);

    const gamesData = data.data;

    if (isDecentralizedEndpoint) {
      setCombinedData(prevCombinedData => {
        const categoryId: string = isCustomCategory
          ? isCustomCategoryAll
            ? generateCategoryAllSelectorKey(data.config.params.provider)
            : selectedCategoryRef.current
          : isGamesDecentralized(gamesData)
          ? gamesData.games?.[0]?.categoryId || ''
          : '';

        const mappedGames = (
          isGamesDecentralized(gamesData) ? gamesData.games : []
        ).map((game: DecentralizedCasinoGame) => {
          return gameMapper({
            ...CASINO_GAME_TEMPLATE,
            extearnal_game_id: game.externalGameId.toString(),
            types: {
              realMode: 0,
              funMode: 0
            },
            icon_1: game.icon1,
            icon_2: game.icon2,
            icon_3: game.icon3,
            provider: 'ALL',
            provider_badge: null,
            provider_title: 'ALL',
            show_as_provider: 'ALL',
            cats: game.categories.map(decentralizedGameCategoriesMapper)
          });
        });

        if (prevCombinedData.queryKey !== queryKey) {
          return {
            queryKey: queryKey,
            data: {
              ...data,
              data: {
                status: gamesData.status,
                games: {
                  [categoryId]: {
                    count: isGamesDecentralized(gamesData)
                      ? gamesData.count
                      : 0,
                    games: _setUniqueGames(mappedGames)
                  }
                }
              }
            }
          };
        }

        return {
          queryKey: queryKey,
          data: {
            ...data,
            data: {
              status: gamesData.status,
              games: {
                [categoryId]: {
                  count: isGamesDecentralized(gamesData) ? gamesData.count : 0,
                  games: _setUniqueGames([
                    ...(prevCombinedData.data?.data?.games?.[categoryId]
                      ?.games || []),
                    ...(mappedGames || [])
                  ])
                }
              }
            }
          }
        };
      });

      return;
    }

    // when fetching direct games without grouping by categories
    if (
      [ENewCasinoGamesFetchingParamsKey.Id].includes(data.config.params.by_key)
    ) {
      setCombinedData(prevCombinedData => {
        const categoryId: string = isCustomCategory
          ? isCustomCategoryAll
            ? generateCategoryAllSelectorKey(data.config.params.provider)
            : selectedCategoryRef.current
          : isGamesGroupedById(gamesData)
          ? configs.groupAllByCategories
            ? gamesData.games?.[0]?.cats?.[0]?.id || ''
            : CUSTOM_CATEGORIES.all.id
          : '';

        if (prevCombinedData.queryKey !== queryKey) {
          return {
            queryKey: queryKey,
            data: {
              ...data,
              data: {
                status: gamesData.status,
                games: {
                  [categoryId]: {
                    count: Number(
                      (isGamesGroupedById(gamesData)
                        ? gamesData
                        : { total_count: '' }
                      ).total_count
                    ),
                    games: _setUniqueGames(
                      (isGamesGroupedById(gamesData)
                        ? gamesData.games
                        : []
                      ).map(gameMapper)
                    )
                  }
                }
              }
            }
          };
        }

        return {
          queryKey: queryKey,
          data: {
            ...data,
            data: {
              status: gamesData.status,
              games: {
                ...(configs.groupAllByCategories
                  ? prevCombinedData.data?.data?.games || {}
                  : {}),
                [categoryId]: {
                  count: Number(
                    (isGamesGroupedById(gamesData)
                      ? gamesData
                      : { total_count: '' }
                    ).total_count
                  ),
                  games: _setUniqueGames([
                    ...(prevCombinedData.data?.data?.games?.[categoryId]
                      ?.games || []),
                    ...(isGamesGroupedById(gamesData)
                      ? gamesData.games
                      : []
                    ).map(gameMapper)
                  ])
                }
              }
            }
          }
        };
      });

      return;
    }

    // when fetching games with grouping by categories
    setCombinedData(prevCombinedData => {
      if (prevCombinedData.queryKey !== queryKey) {
        const mappedGames: TCasinoGamesCombinedDataPartition['data']['games'] =
          {};

        Object.entries(
          isGamesGroupedByCategory(gamesData) ? gamesData.games : {}
        ).map(([categoryId, games]) => {
          mappedGames[categoryId] = {
            count: Number(games.count),
            games: _setUniqueGames(games.games.map(gameMapper))
          };
        });

        return {
          queryKey: queryKey,
          data: {
            ...data,
            data: {
              status: gamesData.status,
              games: mappedGames
            }
          }
        };
      }

      const allGames: TCasinoGamesCombinedDataPartition['data']['games'] =
        prevCombinedData.data?.data?.games || {};

      const mergedGames = Object.keys(allGames).reduce((acc, categoryId) => {
        const games = allGames[categoryId];
        acc[categoryId] = {
          count: Number(
            isGamesGroupedByCategory(gamesData)
              ? gamesData?.games?.[categoryId]?.count || games.count
              : games.count
          ),
          games: _setUniqueGames([
            ...(games.games || []),
            ...(isGamesGroupedByCategory(gamesData)
              ? gamesData.games?.[categoryId]?.games || []
              : []
            ).map(gameMapper)
          ])
        };

        return acc;
      }, {} as TCasinoGamesCombinedDataPartition['data']['games']);

      return {
        queryKey: queryKey,
        data: {
          ...data,
          data: {
            status: gamesData.status,
            games: mergedGames
          }
        }
      };
    });
  }, [_data]);

  return { combinedData, setCombinedData };
};
